// اللَّهُ لَا إِلَٰهَ إِلَّا هُوَ الْحَيُّ الْقَيُّومُ ۚ لَا تَأْخُذُهُ سِنَةٌ وَلَا نَوْمٌ ۚ
// لَهُ مَا فِي السَّمَاوَاتِ وَمَا فِي الْأَرْضِ ۗ مَنْ ذَا الَّذِي يَشْفَعُ عِنْدَهُ إِلَّا بِإِذْنِهِ ۚ
// يَعْلَمُ مَا بَيْنَ أَيْدِيهِمْ وَمَا خَلْفَهُمْ ۖ وَلَا يُحِيطُونَ بِشَيْءٍ مِنْ عِلْمِهِ إِلَّا بِمَا شَاءَ ۚ
// وَسِعَ كُرْسِيُّهُ السَّمَاوَاتِ وَالْأَرْضَ ۖ وَلَا يَئُودُهُ حِفْظُهُمَا ۚ وَهُوَ الْعَلِيُّ الْعَظِيمُ
// (Bakara 255)

import { useEffect, useRef, useState, lazy, Suspense, type SubmitEvent, type ReactElement } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '@/api/authService';
import { useAuthStore } from '@/store/authStore';
import { useActiveContextStore } from '@/store/activeContextStore';
import { companyService } from '@/api/companyService';
import { extractErrorMessage, CIKIS_SEBEBI_ANAHTARI } from '@/api/client';
import { useBrand } from '@/hooks/useBrand';
import { Mail, Lock, LogIn, Building2, KeyRound, Info, MonitorSmartphone } from 'lucide-react';
// Barrel (@/components/shared) YERINE dogrudan modul: barrel EntityAutocomplete,
// ReflectionPreviewPanel, PosShell gibi agir uyeleri de zincire baglayip giris ekranini
// gereksiz yere buyutuyordu (olculdu 2026-08-31: giris sayfasi 61 JS parcasi / 389 KB cekiyordu,
// icinde EntityFormPanel 76 KB ve ListRenderer 17 KB vardi — giris formunun hicbirine ihtiyaci yok).
import { AuthShell } from '@/components/shared/AuthShell';
import { Field, TextInput, PasswordInput } from '@/components/shared/FormControls';
import { Button } from '@/components/shared/Button';
// Entegrator onboarding YALNIZ 'onboard' sekmesinde cizilir; ilk boyayi bekletmesin diye tembel.
const IntegratorOnboarding = lazy(() =>
  import('@/components/onboarding/IntegratorOnboarding').then((m) => ({ default: m.IntegratorOnboarding })));
import { useTranslation } from '@/lib/i18n';

const REMEMBER_KEY = 'elpo_remember';
const DEMO_PASSWORD = '123456';

// Eski surum parolayi da yaziyordu; okurken parola alani kayittan silinir ki tarayicida kalmasin.
function readRemembered(): { firmCode?: string; email?: string } {
  try {
    const raw = JSON.parse(localStorage.getItem(REMEMBER_KEY) ?? '{}') as { firmCode?: string; email?: string; password?: string };
    if (raw.password !== undefined) {
      localStorage.setItem(REMEMBER_KEY, JSON.stringify({ firmCode: raw.firmCode, email: raw.email }));
    }
    return { firmCode: raw.firmCode, email: raw.email };
  } catch {
    return {};
  }
}

// Tek oturum: client.ts eski oturumu kapatirken sebebi buraya yazar. Okuma ve silme ayri:
// StrictMode baslaticiyi iki kez cagirir, silme baslaticida olsaydi ikinci cagri bos donerdi.
function readSignOutReason(): string | null {
  try { return sessionStorage.getItem(CIKIS_SEBEBI_ANAHTARI); } catch { return null; }
}

export function LoginPage(): ReactElement {
  const { t } = useTranslation();
  const brand = useBrand();
  const saved = readRemembered();
  // Demo banner dev + test ortamlarinda — PROD'da asla gorunmez.
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  // localhost da dahil: gelistirme local'de yapiliyor, demo kutusu orada da gorunmeli.
  // Prod'da host asla localhost olmaz — sizinti riski yok.
  const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
  const isTestEnv = isLocalhost || /(^|\.)(dev|test)\./i.test(host) || /(dev|test)\.elpo\.com\.tr$/i.test(host);
  // Demo banner yalnız dev/test'te (prod'da asla) — testçiler sisteme girip denesin diye.
  // Subdomain'e gore demo giris bilgisi — her urun kendi demo kullanicisini gosterir (sifre 123456).
  // e-Belge portalinda demo YOK: gosterilen portaltest@elpo.com.tr ESKI sistemin hesabiydi ve herkes ayni hesapla
  // girip tek oturum kurali yuzunden birbirini dusuruyordu; kullanici karari (05.10.2026) "eski sisteme dokunulmaz".
  // Yeni sisteme ozgu bir demo hesabi tanimlaninca buraya yazilir.
  const demoCred = (() => {
    if (/yikamatik/i.test(host)) return { email: 'yikamatikdemo@elpo.com.tr', label: 'Yıkamatik Demo' };
    if (/eportal|ebelge/i.test(host)) return null;
    if (/smarterp/i.test(host)) return { email: 'smarterpdemo@elpo.com.tr', label: 'SmartERP Demo' };
    return { email: 'demo@elpo.com.tr', label: 'Elpo Demo' };
  })();
  const showDemo = isTestEnv && demoCred !== null;
  // Firma kodu normalde SORULMAZ (merkezi kimlik defterinden cozulur). docs/IDENTITY_MODEL.md
  // Ancak kullanicinin e-postasi merkezi kimlik defterinde yoksa backend "Firma kodu gerekli" doner;
  // o durumda firma kodu alani OTOMATIK acilir ki kimse disarida kalmasin (kilitlenme onlenir).
  // Kaydedilmiş firma kodu varsa login'de SESSİZCE gönderilir (arka planda) — alan GÖSTERİLMEZ.
  // needFirm başlangıçta HER ZAMAN false: hesap kodu alanı yalnız backend "Firma kodu gerekli" derse açılır.
  const [firmCode, setFirmCode] = useState<string>(saved.firmCode ?? '');
  const [needFirm, setNeedFirm] = useState<boolean>(false);
  const [email, setEmail] = useState<string>(showDemo && demoCred ? demoCred.email : (saved.email ?? ''));
  const [password, setPassword] = useState<string>('');

  // Demo: subdomain'e gore urun demo kullanicisi (smarterpdemo / yikamatikdemo), sifre 123456.
  // Brand (Yikamatik/SmartERP gorunumu) subdomain'den gelir. Dugme "Demo ile gir" diyor ama
  // yalniz formu dolduruyordu; kullanici bir kez daha "Giris Yap"a basmak zorunda kaliyordu.
  function fillDemo(): void {
    if (!demoCred) return;
    setEmail(demoCred.email);
    setPassword(DEMO_PASSWORD);
    void signIn(demoCred.email, DEMO_PASSWORD);
  }
  const [rememberMe, setRememberMe] = useState(!!saved.email);
  const [error, setError] = useState<string | null>(null);
  const [signOutReason] = useState<string | null>(readSignOutReason);
  // Bir kez gosterilir; yenilemede ya da sonraki giriste tekrar cikmasin.
  useEffect(() => {
    if (signOutReason) try { sessionStorage.removeItem(CIKIS_SEBEBI_ANAHTARI); } catch { /* gizli mod */ }
  }, [signOutReason]);
  // Rehberli akış: kayıt sayfasından "e-posta zaten kayıtlı" ile gelindiyse lisans-ekle bandı göster
  const location = useLocation();
  const guided = location.state as { emailExists?: boolean; email?: string; addLicense?: boolean } | null;
  const [submitting, setSubmitting] = useState(false);
  // Yayın/yeniden başlatma sonrası ilk ~30 sn sunucu açılışı ve şema kontrolüyle meşgul; giriş
  // isteği o arada 28 sn'ye kadar bekledi (ölçüldü). Düğme "yükleniyor" dönerken kullanıcı
  // "olmadı" sanıp tekrar tıklıyordu. 2 sn'yi geçen istekte sebebi söylüyoruz, 10 sn'de
  // hâlâ beklendiğini. Mesaj düğmenin hemen altında: göz düğmede, oraya bakıyor.
  const [beklemeAsamasi, setBeklemeAsamasi] = useState<0 | 1 | 2>(0);
  // State bir sonraki çizime kadar güncellenmez; aynı anda iki tık (form + "Demo ile gir")
  // iki ayrı giriş isteği atmasın diye kilit ref'te tutulur.
  const girisSuruyor = useRef(false);
  const setAuth = useAuthStore((state) => state.setAuth);
  const navigate = useNavigate();
  // Entegratör-destekli app'lerde (e-belge portalı) ilk-kez onboarding sekmesi
  const [tab, setTab] = useState<'login' | 'onboard'>('login');
  // Birleşik akıllı giriş (JIT): ElpoCore girişi tutmazsa aynı e-posta/şifre Entegratör onboarding'e
  // seed edilir → orada otomatik Entegratör doğrulaması → doğrularsa hesap yoksa açılır.
  const [onboardSeed, setOnboardSeed] = useState<{ username: string; password: string } | null>(null);

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    await signIn(email, password);
  }

  async function signIn(email: string, password: string): Promise<void> {
    if (girisSuruyor.current) return;
    girisSuruyor.current = true;
    setError(null);
    setSubmitting(true);
    const ikiSaniye = window.setTimeout(() => setBeklemeAsamasi(1), 2000);
    const onSaniye = window.setTimeout(() => setBeklemeAsamasi(2), 10000);
    try {
      const response = await authService.login({ firmCode: firmCode.trim(), email, password });
      // Parola SAKLANMAZ. "Beni hatirla" eskiden parolayi da duz metin olarak localStorage'a
      // yaziyordu: tarayiciya erisen her betik ve her kisi okuyabiliyordu.
      if (rememberMe) {
        localStorage.setItem(REMEMBER_KEY, JSON.stringify({ firmCode: firmCode.trim(), email }));
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
      setAuth(response.token, response.user);

      // Entegratör-destekli markada (eportal) girişten sonra kayıtlı entegratör kimliği DOĞRULANIR:
      // kayıt yok ya da kimlik geçersizse kullanıcı uyarıyla /einvoice/credentials'a yönlendirilir
      // (kullanıcı kararı 2026-07-16 — kutular sessizce boş kalmasın). Ağ/sunucu hatasında giriş bloklanmaz.
      // Entegratör-destekli markada (eportal) kayıtlı entegratör kimliği doğrulanır; kimlik yoksa
      // ya da geçersizse kullanıcı uyarıyla /einvoice/credentials'a alınır (kullanıcı kararı
      // 2026-07-16 — kutular sessizce boş kalmasın).
      //
      // BU DOĞRULAMA GİRİŞİ BEKLETMEZ (2026-08-31). Eskiden `await` ile zincirdeydi: giriş isteği
      // bitiyor, ardından firma listesi, ardından DIŞ entegratör ağ geçidine bağlantı testi
      // bekleniyor ve ekran ancak ondan sonra açılıyordu. Uzak entegratör yavaşladığında kullanıcı
      // "giriş yaptım ama sistem açılmıyor" durumunda kalıyordu. Artık uygulama HEMEN açılır,
      // doğrulama arka planda koşar ve yalnız BAŞARISIZSA kullanıcıyı kimlik ekranına alır.
      if (brand.integratorOnboarding) {
        void (async () => {
          // Entegratör testi firma bazlıdır (X-Active-Company-Id → VKN). Giriş hemen sonrasında
          // aktif firma bağlamı henüz kurulmamış olabilir → header boş gider ve test yanlışlıkla
          // "kimlik bulunamadı" döner. Bu yüzden testten ÖNCE aktif firmayı kur.
          try {
            const list = await companyService.accessible();
            const ac = useActiveContextStore.getState();
            const stillValid = !!ac.activeCompanyId && list.some((c) => c.id === ac.activeCompanyId);
            if (!stillValid && list.length > 0) {
              const def = list.find((c) => c.isDefault) ?? list[0];
              if (def) ac.setCompany(def.id, def.displayName, def.currency);
            }
          } catch {
            // Firma listesi alınamadı (ağ/geçici) — yine de testi dene.
          }

          try {
            const { testConnection } = await import('@/api/ebelgeInvoiceService');
            const test = await testConnection();
            if (!test.success) {
              const detail = test.steps?.find((s) => !s.ok)?.detail;
              navigate('/einvoice/credentials', {
                replace: true,
                state: {
                  integratorWarning:
                    'Entegratör kimliğiniz doğrulanamadı — e-Belge işlemleri için bilgilerinizi girin.'
                    + (detail ? ` (${detail})` : ''),
                },
              });
            }
          } catch {
            // test ucuna ulaşılamadı (ağ/geçici) — kullanıcıyı rahatsız etme, normal akış sürsün
          }
        })();
      }

      navigate(brand.homeRoute || '/', { replace: true });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const msg = extractErrorMessage(err, 'Giriş başarısız');
      const needsFirmCode = /firma kodu/i.test(msg);
      // JIT birleşik giriş: Entegratör-destekli app'te portal girişi tutmadıysa (şifre yanlış = 401,
      // veya e-posta merkezi defterde yok = "firma kodu gerekli") aynı bilgilerle Entegratör'ü dene.
      // Yeni Entegratör kullanıcısının firma kodu YOKTUR → firma kodu sormak yerine onboarding'e yönlendir;
      // Entegratör doğrularsa hesap yoksa açılır, doğrulamazsa onboarding'de "geçersiz" uyarısı çıkar.
      if (brand.integratorOnboarding && (status === 401 || needsFirmCode)) {
        setOnboardSeed({ username: email, password });
        setTab('onboard');
        setError(null);
      } else if (needsFirmCode) {
        // Hesap kodu artık e-postadan çözülür; bu hata YALNIZ aynı e-posta birden çok hesapta
        // varsa gelir (hangisi olduğunu kullanıcı seçer).
        setNeedFirm(true);
        setError('Bu e-posta birden çok hesapta kayıtlı — hesap kodunuzu girip tekrar deneyin.');
      } else {
        // Sebep hesap kodu DEĞİL (ör. parola yanlış): kod alanı açık kalırsa ekranda iki çelişkili
        // mesaj olur ("parola yanlış" + "e-postanız defterde yok") ve kullanıcı olmayan bir kod arar.
        setNeedFirm(false);
        setFirmCode('');
        setError(msg);
      }
    } finally {
      window.clearTimeout(ikiSaniye);
      window.clearTimeout(onSaniye);
      setBeklemeAsamasi(0);
      girisSuruyor.current = false;
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (brand.displayName) document.title = brand.displayName;
    // Sekme ikonu BURADA DEĞİŞTİRİLMEZ: favicon tüm Elpo ürünlerinde aynı dosya
    // (index.html + markaKimliginiUygula). Burası marka kaydındaki ürün faviconunu
    // yazıyordu, ELPO ikonu bu yüzden kayboluyordu.
  }, [brand.displayName]);

  return (
    <AuthShell
      title={t('login.welcomeProduct', { urun: brand.displayName, defaultValue: brand.loginGreeting ?? 'Hoş geldiniz' })}
      subtitle={t(`login.brandSubtitle.${brand.subdomain}`, { defaultValue: brand.loginSubtitle ?? t('login.subtitle') })}
      error={error}
      footer={
        <div className="text-center text-[13px] text-muted-foreground">
          {t('login.hesabinYokMu')}{' '}
          <Link to="/register" className="font-semibold hover:underline" style={{ color: brand.primaryColor }}>
            {t('login.kaydol', { urun: brand.displayName })}
          </Link>
        </div>
      }
    >
      {/* Oturum kapatildi bilgisi. OpenDesign tek kolonlu, formun en ustunde bir info-box onerdi
          (alindi) ama ikonu alert-triangle'di; frontend-design uyari dili yerine bilgi tonu dedi
          (alindi): kullanici yanlis bir sey yapmadi, kirmizi/sari onu suclu hissettirir. Bu yuzden
          turkuaz ince cerceve + cihaz ikonu, role=status (ekran okuyucu sakin okur, alert degil). */}
      {signOutReason && (
        <div role="status" className="mb-4 rounded-xl border border-[#0091B3]/40 bg-[#0091B3]/[0.06] p-3.5">
          <div className="flex items-start gap-2.5">
            <MonitorSmartphone className="h-4 w-4 mt-0.5 shrink-0 text-[#0091B3]" aria-hidden="true" />
            <div className="text-[13px]">
              <div className="font-semibold text-foreground mb-0.5">Oturumunuz kapatıldı</div>
              <div className="text-muted-foreground">{signOutReason} Devam etmek için yeniden giriş yapın.</div>
            </div>
          </div>
        </div>
      )}
      {guided?.emailExists && (
        <div className="mb-4 rounded-xl border-2 p-3.5" style={{ borderColor: brand.primaryColor, background: `color-mix(in srgb, ${brand.primaryColor} 8%, transparent)` }}>
          <div className="flex items-start gap-2.5">
            <KeyRound className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brand.primaryColor }} />
            <div className="text-[13px]">
              <div className="font-bold text-foreground mb-0.5">Bu e-posta zaten kayıtlı</div>
              <div className="text-muted-foreground">
                {guided.email ? <span className="font-semibold text-foreground">{guided.email}</span> : 'Hesabınız'} ile <b>giriş yapın</b> — {brand.displayName} lisansınız hesabınıza <b>eklenecek</b>.
              </div>
            </div>
          </div>
        </div>
      )}
      {brand.integratorOnboarding && tab === 'onboard' ? (
        <Suspense fallback={<div className="py-10 text-center text-sm text-slate-500">Yükleniyor…</div>}>
          <IntegratorOnboarding
            onSwitchToLogin={() => { setOnboardSeed(null); setTab('login'); }}
            initialUsername={onboardSeed?.username}
            initialPassword={onboardSeed?.password}
            autoVerify={!!onboardSeed}
          />
        </Suspense>
      ) : (
        <>
      {showDemo && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3.5">
          <div className="flex items-center gap-2 mb-1.5">
            <Info className="h-4 w-4 shrink-0 text-amber-700" />
            <span className="text-[13px] font-bold text-amber-900">{t('login.demoBaslik')}</span>
          </div>
          <p className="text-[11.5px] text-amber-800 leading-relaxed mb-2">
            {t('login.demoAciklama')}
          </p>
          <div className="flex items-center justify-between gap-2 bg-white/70 rounded-lg px-2.5 py-1.5 border border-amber-200">
            <div className="text-[11px] text-amber-900 font-mono leading-tight">
              <div>{t('login.kullanici')}: <span className="font-semibold">{demoCred?.email}</span></div>
              <div>{t('login.password')}: <span className="font-semibold">123456</span></div>
            </div>
            <button
              type="button"
              onClick={fillDemo}
              disabled={submitting}
              className="flex-shrink-0 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors disabled:opacity-60 disabled:cursor-wait"
            >
              {t('login.demoIleGir')}
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label={t('login.email')}>
          <TextInput icon={Mail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="email" placeholder="demo@elpo.com.tr" />
        </Field>
        <Field label={t('login.password')}>
          <PasswordInput icon={Lock} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" placeholder="••••••••••••" />
        </Field>
        {needFirm && (
          <Field label="Hesap Kodu" hint="Bu e-posta birden çok hesapta kayıtlı; hangisine gireceğinizi belirtin.">
            <TextInput icon={Building2} value={firmCode} onChange={(e) => setFirmCode(e.target.value.toUpperCase())} autoComplete="organization" placeholder="Örn. ELPOOTO01" />
          </Field>
        )}
        {/* Hesap kodu login'de SORULMAZ — e-postadan merkezi defterde otomatik çözülür.
            "Hesap kodum var" manuel toggle KALDIRILDI (kullanıcı kararı). Kod alanı yalnız otomatik
            çözüm gerçekten başarısızsa (backend "need firm") otomatik görünür (yukarıda needFirm). */}
        <div className="flex items-center">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-border text-primary-600 focus:ring-primary-500" />
            <span className="text-[12px] text-muted-foreground">{t('login.rememberMe')}</span>
          </label>
        </div>
        <Button
          type="submit"
          block
          icon={LogIn}
          loading={submitting}
          disabled={!email || !password}
          className="py-3.5 rounded-xl text-white shadow-lg hover:shadow-xl hover:brightness-110 hover:scale-[1.01] active:scale-[0.99]"
          style={{ background: 'linear-gradient(135deg, #003b5c, #0a4d63)' }}
        >
          {submitting ? t('login.submitting') : t('login.submit')}
        </Button>
        {/* OpenDesign ve frontend-design ikisi de aynı yeri önerdi: düğmenin hemen altı, tek satır,
            sakin ton. Uyarı rengi kullanılmadı — bu bir hata değil, bekleme bilgisi. */}
        {beklemeAsamasi > 0 && (
          <p role="status" aria-live="polite" className="flex items-start gap-2 text-[12px] leading-relaxed text-muted-foreground -mt-1">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#0091b3]" />
            <span>{beklemeAsamasi === 1 ? t('login.sunucuHazirlaniyor') : t('login.halaBekleniyor')}</span>
          </p>
        )}
      </form>
        </>
      )}
    </AuthShell>
  );
}
